import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Outlet } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    overflow-x: hidden;
  }
`;

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;

    /* Responsive sizing for different desktop resolutions */
    @media (min-width: 1920px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.25) 0%, transparent 55%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 55%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.18) 0%, transparent 55%);
    }

    @media (min-width: 1440px) and (max-width: 1919px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.22) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.09) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.16) 0%, transparent 50%);
    }

    @media (min-width: 1366px) and (max-width: 1439px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.2) 0%, transparent 45%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 45%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.15) 0%, transparent 45%);
    }

    @media (min-width: 1280px) and (max-width: 1365px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.18) 0%, transparent 40%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.07) 0%, transparent 40%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.13) 0%, transparent 40%);
    }

    @media (min-width: 1024px) and (max-width: 1279px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.16) 0%, transparent 35%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.06) 0%, transparent 35%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.12) 0%, transparent 35%);
    }

    @media (max-width: 1023px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.12) 0%, transparent 30%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.04) 0%, transparent 30%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.08) 0%, transparent 30%);
    }
    
    @media (max-width: 768px) {
      background: 
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 25%),
        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 25%),
        radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.06) 0%, transparent 25%);
    }
  }
`;

const MainContent = styled.div`
  flex: 1;
  transition: margin-left 0.3s ease;
  z-index: 1;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: clamp(2rem, 3vw, 2.5rem);
    margin-left: 280px;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: clamp(1.75rem, 2.5vw, 2.25rem);
    margin-left: 280px;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: clamp(1.5rem, 2vw, 2rem);
    margin-left: 280px;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: clamp(1.25rem, 1.75vw, 1.875rem);
    margin-left: 280px;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: clamp(1rem, 1.5vw, 1.75rem);
    margin-left: 280px;
  }

  @media (max-width: 1023px) {
    padding: clamp(0.875rem, 1.25vw, 1.5rem);
    margin-left: 200px;
  }
  
  @media (max-width: 768px) {
    padding: clamp(0.75rem, 1vw, 1.25rem);
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  max-width: none;
  overflow-x: hidden;
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    border-radius: clamp(1.25rem, 2vw, 1.5rem);
    padding: clamp(2rem, 3vw, 2.5rem);
    min-height: calc(100vh - clamp(3rem, 4vw, 4rem));
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    border-radius: clamp(1.125rem, 1.75vw, 1.375rem);
    padding: clamp(1.75rem, 2.5vw, 2.25rem);
    min-height: calc(100vh - clamp(2.75rem, 3.5vw, 3.75rem));
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    border-radius: clamp(1rem, 1.5vw, 1.25rem);
    padding: clamp(1.5rem, 2vw, 2rem);
    min-height: calc(100vh - clamp(2.5rem, 3vw, 3.5rem));
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    border-radius: clamp(0.875rem, 1.25vw, 1.125rem);
    padding: clamp(1.25rem, 1.75vw, 1.875rem);
    min-height: calc(100vh - clamp(2.25rem, 2.5vw, 3.25rem));
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    border-radius: clamp(0.75rem, 1vw, 1rem);
    padding: clamp(1rem, 1.5vw, 1.75rem);
    min-height: calc(100vh - clamp(2rem, 2.5vw, 3rem));
  }

  @media (max-width: 1023px) {
    border-radius: clamp(0.625rem, 0.875vw, 0.875rem);
    padding: clamp(0.875rem, 1.25vw, 1.5rem);
    min-height: calc(100vh - clamp(1.75rem, 2vw, 2.75rem));
  }
  
  @media (max-width: 768px) {
    border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
    padding: clamp(0.75rem, 1vw, 1.25rem);
    min-height: calc(100vh - clamp(1.5rem, 1.75vw, 2.5rem));
  }
`;

const AdminDashboard = () => {
  return (
    <>
      <GlobalStyle />
      <AdminContainer>
        <Sidebar />
        <MainContent>
          <ContentWrapper>
            <Outlet />
          </ContentWrapper>
        </MainContent>
      </AdminContainer>
    </>
  );
};

export default AdminDashboard; 